import { useMemo, useState } from "react";
import EditModal from "../ui/EditModal";
import { formatDate } from "../../utils/dateUtils";
import { APPOINTMENT_TYPES } from "../forms/doctorVisitForm";

const TYPE_LABEL = Object.fromEntries(APPOINTMENT_TYPES.map((t) => [t.value, t.label]));

const FollowUpBadge = ({ date }) => {
  if (!date) return null;
  const due  = new Date(date);
  const now  = new Date();
  const days = Math.round((due - now) / 86400000);
  const past = days < 0;
  const soon = days >= 0 && days <= 7;
  const color = past ? "danger" : soon ? "warning" : "info";
  const label = past
    ? `Follow-up overdue (${new Date(date).toLocaleDateString("en-GB")})`
    : `Follow-up: ${new Date(date).toLocaleDateString("en-GB")}`;
  return (
    <span className={`badge bg-${color} ms-1`} style={{ fontSize: "0.7rem" }}>
      {label}
    </span>
  );
};

const DoctorVisitTable = ({ data, onUpdate, onDelete }) => {
  const [page, setPage]           = useState(1);
  const [rowsPerPage]             = useState(5);
  const [sortDir, setSortDir]     = useState("desc");
  const [editingItem, setEditingItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving]   = useState(false);

  const sorted = useMemo(
    () =>
      [...data].sort((a, b) => {
        if (a.date < b.date) return sortDir === "asc" ? -1 : 1;
        if (a.date > b.date) return sortDir === "asc" ? 1 : -1;
        return 0;
      }),
    [data, sortDir]
  );

  const pageCount = Math.ceil(sorted.length / rowsPerPage);
  const pageData  = sorted.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const handleEdit = (item) => { setEditingItem(item); setIsModalOpen(true); };

  const handleSave = async (formData) => {
    setIsSaving(true);
    try {
      await onUpdate(editingItem.id, formData);
      setIsModalOpen(false);
      setEditingItem(null);
    } catch { alert("Failed to save changes."); }
    finally { setIsSaving(false); }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this visit record?")) {
      try { await onDelete(id); }
      catch { alert("Failed to delete."); }
    }
  };

  const editFields = [
    { name: "date",            label: "Date & Time",          type: "datetime-local", required: true },
    { name: "appointmentType", label: "Appointment Type",     type: "select", required: true,
      options: APPOINTMENT_TYPES.map((t) => ({ label: t.label, value: t.value })) },
    { name: "practitioner",    label: "Practitioner",         type: "text" },
    { name: "reason",          label: "Reason for Visit",     type: "text", required: true },
    { name: "weight",          label: "Weight (kg)",          type: "number" },
    { name: "height",          label: "Height (cm)",          type: "number" },
    { name: "headCircumference", label: "Head Circumference (cm)", type: "number" },
    { name: "notes",           label: "Outcome / Notes",      type: "textarea" },
    { name: "prescriptions",   label: "Prescriptions / Treatments", type: "textarea" },
    { name: "followUpDate",    label: "Follow-up Date",       type: "date" },
  ];

  return (
    <>
      <div className="table-responsive">
        <table className="table table-sm table-hover">
          <thead className="table-light">
            <tr>
              <th style={{ cursor: "pointer" }} onClick={() => { setSortDir((d) => d === "asc" ? "desc" : "asc"); setPage(1); }}>
                Date {sortDir === "desc" ? "▼" : "▲"}
              </th>
              <th>Type</th>
              <th>Reason</th>
              <th>Practitioner</th>
              <th>Follow-up</th>
              <th style={{ width: "80px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageData.map((row) => (
              <tr key={row.id}>
                <td style={{ whiteSpace: "nowrap" }}>{formatDate(row.date)}</td>
                <td>
                  <span className="badge"
                    style={{ background: "rgba(255,179,217,0.25)", color: "var(--primary-dark)", fontWeight: 600 }}>
                    {TYPE_LABEL[row.appointmentType] || row.appointmentType}
                  </span>
                </td>
                <td style={{ maxWidth: "200px" }}>
                  <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {row.reason}
                  </span>
                  {row.prescriptions && (
                    <small style={{ color: "var(--text-light)", fontSize: "0.75rem" }}>
                      💊 {row.prescriptions.length > 40 ? row.prescriptions.slice(0, 40) + "…" : row.prescriptions}
                    </small>
                  )}
                </td>
                <td>{row.practitioner || <span style={{ color: "var(--text-light)" }}>—</span>}</td>
                <td>
                  {row.followUpDate
                    ? <FollowUpBadge date={row.followUpDate} />
                    : <span style={{ color: "var(--text-light)", fontSize: "0.8rem" }}>None</span>
                  }
                </td>
                <td>
                  <button
                    className="btn btn-xs btn-warning me-1"
                    title="Edit"
                    onClick={() => handleEdit(row)}
                    style={{ padding: "0.25rem 0.5rem", fontSize: "0.875rem" }}
                  >✎</button>
                  <button
                    className="btn btn-xs btn-danger"
                    title="Delete"
                    onClick={() => handleDelete(row.id)}
                    style={{ padding: "0.25rem 0.5rem", fontSize: "0.875rem" }}
                  >✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pageCount > 1 && (
        <nav>
          <ul className="pagination pagination-sm">
            <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
              <button className="page-link" onClick={() => setPage((p) => Math.max(p - 1, 1))}>Previous</button>
            </li>
            {[...Array(pageCount)].map((_, i) => (
              <li key={i + 1} className={`page-item ${i + 1 === page ? "active" : ""}`}>
                <button className="page-link" onClick={() => setPage(i + 1)}>{i + 1}</button>
              </li>
            ))}
            <li className={`page-item ${page === pageCount ? "disabled" : ""}`}>
              <button className="page-link" onClick={() => setPage((p) => Math.min(p + 1, pageCount))}>Next</button>
            </li>
          </ul>
        </nav>
      )}

      <EditModal
        isOpen={isModalOpen}
        item={editingItem}
        title="Edit Doctor Visit"
        fields={editFields}
        onSave={handleSave}
        onCancel={() => { setIsModalOpen(false); setEditingItem(null); }}
        isLoading={isSaving}
      />
    </>
  );
};

export default DoctorVisitTable;
